import React from "react";
import { useNavigate, Link } from "react-router-dom";
import logo from "../assets/logo_basic.png";
import AppFooter from "../components/AppFooter";
import PublicSiteHeader from "../components/PublicSiteHeader";
import SEO from "../components/SEO";

import { createProductSchema } from "../seo/schemas/productSchema";
import { createBreadcrumbSchema } from "../seo/schemas/breadcrumbSchema";

const SITE_URL = "https://www.reignitespirit.com";
const PAGE_PATH = "/snapshot";
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;

export default function SnapshotPage() {
  const navigate = useNavigate();

  const snapshotProductSchema = createProductSchema({
    id: `${PAGE_URL}#product`,

    name: "The Snapshot",

    description:
      "A rapid AI-assisted career audit that evaluates potential AI impact on the user's current role, identifies transferable strengths, suggests one career pathway, and provides skill-gap guidance.",

    image:
      "https://www.reignitespirit.com/og-snapshot-report.jpeg",

    url: PAGE_URL,

    price: "0.00",

    currency: "USD",

    category: "Career Intelligence Report",
  });

  const breadcrumbSchema = createBreadcrumbSchema({
    pageUrl: PAGE_URL,

    items: [
      {
        name: "Home",
        url: `${SITE_URL}/landing`,
      },
      {
        name: "The Snapshot",
        url: PAGE_URL,
      },
    ],
  });

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${PAGE_URL}#webpage`,
    url: PAGE_URL,
    name: "The Snapshot",
    description:
      "A rapid AI-assisted career audit for professionals who want a focused view of AI-related role impact, transferable strengths, career direction and skill gaps.",

    isPartOf: {
      "@id": `${SITE_URL}/#website`,
    },

    about: {
      "@id": `${PAGE_URL}#product`,
    },

    breadcrumb: {
      "@id": `${PAGE_URL}#breadcrumb`,
    },

    inLanguage: "en",
  };

  const goToRegister = () => {
    localStorage.setItem("selectedPlan", "snapshot");
    navigate("/register?plan=snapshot");
  };

  return (
    <div className="min-h-screen bg-[#11111f] text-slate-300">
      <SEO
        title="The Snapshot"
        description="Get a rapid AI-assisted career audit covering role impact, transferable strengths, one career pathway and practical skill-gap guidance."
        path={PAGE_PATH}
        image="/og-snapshot-report.jpeg"
        type="product"
        author="ReigniteSpirit"
        keywords="career snapshot report, AI career audit, career transition report, AI job impact assessment, skill gap assessment, career pathway report"
        robots="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        schema={[
          webPageSchema,
          snapshotProductSchema,
          breadcrumbSchema,
        ]}
      />
      <PublicSiteHeader />

      <main className="max-w-5xl mx-auto px-4 py-12 md:py-16">
        <nav
          aria-label="Breadcrumb"
          className="text-sm text-slate-400 mb-10"
        >
          <Link
            to="/landing"
            className="hover:text-[#EB6AB5]"
          >
            Home
          </Link>

          <span
            className="mx-2"
            aria-hidden="true"
          >
            /
          </span>

          <span aria-current="page">
            The Snapshot
          </span>
        </nav>

        <header className="text-center mb-12">
          <span className="inline-block px-4 py-1 rounded-full bg-[#cb01bd]/10 text-[#cb01bd] font-bold text-sm mb-4">
            QUICK CLARITY
          </span>

          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6">
            The Snapshot
          </h1>

          <p className="max-w-3xl mx-auto text-lg text-slate-400 leading-relaxed">
            A focused, fast AI-assisted career audit for professionals who
            want an immediate view of how AI may affect their current role
            and what practical career direction they can consider next.
          </p>
        </header>

        <section
          className="glass-card rounded-3xl border border-slate-700 p-8 md:p-10 bg-[#1a1a2e]"
          aria-labelledby="snapshot-offer-heading"
        >
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2
                id="snapshot-offer-heading"
                className="text-2xl font-bold text-white mb-4"
              >
                Snapshot Career Report
              </h2>

              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-4xl md:text-5xl font-extrabold text-slate-400 line-through decoration-red-500 decoration-4">
                    US$9.99
                  </span>

                  <span className="text-base text-slate-500 font-normal">
                    one-time
                  </span>
                </div>

                <div className="text-5xl font-extrabold text-emerald-400 mt-3">
                  FREE
                </div>

                <p className="text-[#fbbf24] font-bold mt-2">
                  Launch Special — Limited Time
                </p>
              </div>

              <p className="text-slate-400 mb-8">
                Designed for professionals who want a quick, structured
                assessment of their current career position.
              </p>

              <button
                type="button"
                onClick={goToRegister}
                className="w-full md:w-auto px-8 py-4 border border-[#cb01bd] text-[#cb01bd] font-bold rounded-xl hover:bg-[#cb01bd] hover:text-white transition"
              >
                Get Snapshot →
              </button>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-6">
                What you get
              </h2>

              <ul className="space-y-4">
                <li className="text-slate-300">
                  ✓ AI-related job impact assessment
                </li>

                <li className="text-slate-300">
                  ✓ Transferable and distinctive skills assessment
                </li>

                <li className="text-slate-300">
                  ✓ One suggested AI-resilient career pathway relevant to
                  your selected location
                </li>

                <li className="text-slate-300">
                  ✓ Skill-gap assessment and suggested reskilling areas
                </li>

                <li className="text-slate-300">
                  ✓ Access to eligible supporting tools available with the
                  Snapshot plan
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section
          className="mt-12 grid md:grid-cols-3 gap-6"
          aria-labelledby="snapshot-details-heading"
        >
          <h2
            id="snapshot-details-heading"
            className="sr-only"
          >
            Snapshot report details
          </h2>

          <article className="rounded-2xl border border-slate-700 bg-[#1a1a2e] p-6">
            <h3 className="text-xl font-bold text-white mb-3">
              Who it is for
            </h3>

            <p className="text-slate-400 leading-relaxed">
              Professionals who need an initial career direction before
              committing to a more detailed assessment.
            </p>
          </article>

          <article className="rounded-2xl border border-slate-700 bg-[#1a1a2e] p-6">
            <h3 className="text-xl font-bold text-white mb-3">
              What it helps clarify
            </h3>

            <p className="text-slate-400 leading-relaxed">
              Potential AI exposure, transferable strengths, practical
              career direction and areas where additional learning may be
              useful.
            </p>
          </article>

          <article className="rounded-2xl border border-slate-700 bg-[#1a1a2e] p-6">
            <h3 className="text-xl font-bold text-white mb-3">
              Important limitation
            </h3>

            <p className="text-slate-400 leading-relaxed">
              The report is an informational career-planning aid. It does
              not guarantee employment, salary, promotion or a specific
              career outcome.
            </p>
          </article>
        </section>

        <section
  className="mt-12 rounded-3xl border border-[#EB6AB5]/20 bg-[#1a1a2e] p-8"
  aria-labelledby="snapshot-related-resources"
>
  <h2
    id="snapshot-related-resources"
    className="text-2xl font-bold text-[#EB6AB5] mb-6"
  >
    Continue your career planning
  </h2>

  <div className="grid sm:grid-cols-2 gap-4">
    <Link
      to="/deep-audit"
      className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
    >
      <h3 className="font-bold text-white mb-2">
        Deep Career Audit
      </h3>

      <p className="text-sm text-slate-400 leading-relaxed">
        Explore a more detailed career transition analysis with
        additional pathways, skill-gap guidance and action planning.
      </p>
    </Link>

    <Link
      to="/methodology"
      className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
    >
      <h3 className="font-bold text-white mb-2">
        Our Methodology
      </h3>

      <p className="text-sm text-slate-400 leading-relaxed">
        Review how career exposure, transferable skills and pathway
        recommendations are evaluated.
      </p>
    </Link>

    <Link
      to="/runway-calculator"
      className="block rounded-xl border border-slate-700 p-5 hover:border-[#EB6AB5] transition"
    >
      <h3 className="font-bold text-white mb-2">
        Financial Runway Calculator
      </h3>

      <p className="text-sm text-slate-400 leading-relaxed">
        Estimate how long your financial resources may support a
        planned career transition.
      </p>
    </Link>
  </div>
</section>
      </main>

      <AppFooter
        variant="landing"
        logoSrc={logo}
      />
    </div>
  );
}